"use client";

import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import DocxThumbnail from "@/components/common/DocxThumbnail";
import PdfThumbnail from "@/components/common/PdfThumbnail";
import {usePreviewFetch} from "@/hooks/useFilePreview";
import {useInView} from "@/hooks/useInView";
import {parseTextFromResponse, parseXlsxFromResponse, type ExcelSheet} from "@/utils/filePreview";
import {detectFileType, isHtmlFile} from "@/utils/fileTypeDetection";
import ExcelTable from "./ExcelTable";
import InlineHtmlPreview from "./InlineHtmlPreview";

const PREVIEW_ROW_LIMIT = 5;
const PREVIEW_TEXT_BYTES = 8 * 1024;

const PeekSpinner: React.FC = () => (
   <div className="h-24 flex items-center justify-center">
      <div className="w-5 h-5 border-2 border-300 border-t-primary rounded-full animate-spin" />
   </div>
);

const PeekMessage: React.FC<{label: string}> = ({label}) => (
   <div className="text-xs text-500 py-2">{label}</div>
);

const ExcelPeek: React.FC<{url: string}> = ({url}) => {
   const {t} = useTranslation("ask");
   const {data: sheets, error, loading} = usePreviewFetch<ExcelSheet[]>(url, (res) =>
      parseXlsxFromResponse(res, {rowsPerSheet: PREVIEW_ROW_LIMIT, sheetLimit: 1})
   );

   if (loading) return <PeekSpinner />;
   if (error || !sheets) return <PeekMessage label={t("preview.unavailable")} />;

   const rows = sheets[0]?.rows ?? [];
   if (rows.length === 0) return <PeekMessage label={t("preview.emptySpreadsheet")} />;

   return (
      <div className="overflow-hidden">
         <ExcelTable rows={rows} density="peek" />
      </div>
   );
};

const TextPeek: React.FC<{url: string}> = ({url}) => {
   const {t} = useTranslation("ask");
   const {data, error, loading} = usePreviewFetch(url, (res) =>
      parseTextFromResponse(res, PREVIEW_TEXT_BYTES)
   );

   if (loading) return <PeekSpinner />;
   if (error || !data) return <PeekMessage label={t("preview.unavailable")} />;

   return (
      <pre className="text-[11px] font-mono leading-5 text-700 whitespace-pre-wrap break-words line-clamp-[8]">
         {data.content}
      </pre>
   );
};

const ImagePeek: React.FC<{url: string; alt: string}> = ({url, alt}) => {
   const {t} = useTranslation("ask");
   const [failed, setFailed] = useState(false);

   if (failed) return <PeekMessage label={t("preview.unavailable")} />;

   return (
      // `loading="lazy"` lets the browser skip off-viewport images even when the
      // card itself is already in the DOM. `next/image` isn't used because the
      // URL is a user-generated signed download URL with unknown dimensions and
      // arbitrary host — matches the rest of the codebase's dynamic-media usage.
      // eslint-disable-next-line @next/next/no-img-element
      <img
         src={url}
         alt={alt}
         loading="lazy"
         onError={() => setFailed(true)}
         className="max-h-56 w-auto rounded-md object-contain mx-auto"
      />
   );
};

const renderPeek = (
   url: string,
   fileName: string,
   mimeType: string,
   t: (key: string) => string
) => {
   const fileType = detectFileType(mimeType);
   if (isHtmlFile(mimeType, fileName)) {
      return <InlineHtmlPreview key={url} url={url} fileName={fileName} peek />;
   }
   if (fileType.isExcel) return <ExcelPeek url={url} />;
   if (fileType.isPdf) return <PdfThumbnail file={url} width={560} />;
   if (fileType.isDocx) return <DocxThumbnail url={url} scale={0.55} />;
   if (fileType.isImage) return <ImagePeek url={url} alt={fileName} />;
   if (fileType.isText) return <TextPeek url={url} />;
   if (fileType.isPptx) return <PeekMessage label={t("preview.peekPresentation")} />;
   if (fileType.isAudio) return <PeekMessage label={t("preview.peekAudio")} />;
   if (fileType.isVideo) return <PeekMessage label={t("preview.peekVideo")} />;
   return <PeekMessage label={t("clickToPreview")} />;
};

// The card peek can pull non-trivial amounts of data per artifact (full xlsx
// parse, docx render, pdfjs first page). We only start that work once the
// card is on-screen, so long transcripts with many artifacts don't fan out
// network + CPU on mount.
const ArtifactCardPreview: React.FC<{
   url: string;
   fileName: string;
   mimeType: string;
}> = ({url, fileName, mimeType}) => {
   const {t} = useTranslation("ask");
   const [ref, inView] = useInView<HTMLDivElement>("200px");

   return (
      <div ref={ref} className="min-h-[72px]">
         {inView ? renderPeek(url, fileName, mimeType, t) : <PeekSpinner />}
      </div>
   );
};

export default ArtifactCardPreview;
